import {
  formatProjectArea,
  optionalDate,
  getMemberDisplayName,
} from "@utils/data-display";
import { isCompanyContext } from "@utils/type-guards";
import { BaseProjectProps } from "@custom-types/base-types";
import { DateTimeUtils } from "@stellar/utils";
import { ProjectInfoActions } from "@pages/project-profile/tab-contents/project-info-tab/project-info-actions";
import { CopyTextButton } from "@components/copy-text-button";
import { GeneralProfileEvents } from "@utils/track-event/track-event-list";
import { InternalLinkText } from "@components/links/internal-links";
import { ProfileInfoTab } from "@components/profile-info-tab";
import { useMemo } from "react";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import { formatProjectStorageSize } from "@utils/project-utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/** Content inside the information tab of project profile */
export function ProjectInfo({ project }: BaseProjectProps): React.JSX.Element {
  const projectManager = useMemo(() => {
    if (FaroCoreApiUtils.Member.isTeam(project.projectManager)) {
      return `${project.projectManager.name} (Team)`;
    } else if (FaroCoreApiUtils.Member.isMemberActive(project.projectManager)) {
      return (
        <InternalLinkText
          type="USER"
          memberId={project.projectManager.identity}
          location="project profile info tab"
          text={getMemberDisplayName(project.projectManager)}
        />
      );
    } else {
      return (
        <TextWithTooltip
          text={project.projectManager.email}
          suffix="(Pending)"
        />
      );
    }
  }, [project.projectManager]);

  return (
    <ProfileInfoTab
      contents={[
        {
          label: "ID",
          value: project.id,
          action: (
            <CopyTextButton
              textToCopy={project.id}
              successMessage="Project ID copied to clipboard"
              failedMessage="Error: Failed to copy project ID to clipboard"
              trackEventName={{
                name: GeneralProfileEvents.copyToClipboard,
                props: {
                  entityKey: "Id",
                  location: "project profile info tab",
                  target: "PROJECT",
                },
              }}
            />
          ),
          shouldAddDividerBelow: true,
        },
        ...(isCompanyContext(project.context)
          ? [
              {
                label: "Workspace",
                value: (
                  <InternalLinkText
                    type="COMPANY"
                    companyId={project.context.company.id}
                    location="project profile info tab"
                    text={project.context.company.name}
                  />
                ),
              },
              {
                label: "Group",
                value: project.context.group.name,
              },
            ]
          : []),
        {
          label: "Project Manager",
          value: projectManager,
          shouldAddDividerBelow: true,
        },
        {
          label: "Archiving States",
          value: FaroCoreApiUtils.Project.StateDisplayNames[project.state],
        },
        {
          label: "Access Level",
          value:
            FaroCoreApiUtils.Project.AccessLevelDisplayNames[
              project.accessLevel
            ],
          shouldAddDividerBelow: true,
        },
        {
          label: "Address",
          value: project.address,
        },
        {
          label: "Area",
          value: formatProjectArea(project.area),
          shouldAddDividerBelow: true,
        },
        {
          label: "Created",
          value: DateTimeUtils.formatDateTimeInUtc({
            date: project.createdAt,
          }),
        },
        {
          label: "Last Edited",
          value: DateTimeUtils.formatDateTimeInUtc({
            date: project.modifiedAt,
          }),
        },
        {
          label: "Last Captured",
          value: optionalDate({ date: project.lastCapturedAt }),
        },
        {
          label: "Last Download",
          value: optionalDate({ date: project.lastDownloadedAt }),
        },
        {
          label: "Storage Size",
          value: formatProjectStorageSize(project.storage?.size),
        },
      ]}
      actionButtons={<ProjectInfoActions project={project} />}
    />
  );
}
